import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { confirmPasswordReset } from '../lib/auth.js';

export function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialEmail = params.get('email') ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(email, code, newPassword);
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <h1 className="text-3xl font-display text-brand-800 mb-6">Reset password</h1>
      <p className="text-sm text-brand-700 mb-4">
        Check your email for a verification code, then enter it below along with your new password.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email" type="email" value={email} onChange={setEmail} required />
        <Field label="Verification code" type="text" value={code} onChange={setCode} required autoFocus />
        <Field label="New password" type="password" value={newPassword} onChange={setNewPassword} required />
        {error && <div className="text-sm text-red-700">{error}</div>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white px-4 py-2 font-display font-medium"
        >
          {submitting ? 'Resetting…' : 'Reset password'}
        </button>
        <div className="text-sm">
          <Link to="/login" className="text-brand-600 hover:underline">
            Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
  required,
  autoFocus,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-brand-700 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoFocus={autoFocus}
        className="w-full border border-brand-200 px-3 py-2 outline-none focus:border-brand-500 bg-white"
      />
    </label>
  );
}
